import classNames from 'classnames';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import styles from './hotel-distances.module.scss';

export interface HotelDistancesProps {
  /** The text to show as a headline */
  headline?: Content.Components['HotelDistanceFactsDto']['headline'];
  /** The list of items to show */
  items?: Content.Components['HotelDistanceFactsDto']['distances'];
  /** The link to show */
  link?: Content.Components['LinkDto'];
  /** The rich text to show as a description */
  text?: string;
}

const HotelDistances = ({
  headline,
  items = [],
  link,
  text,
  ...rest
}: HotelDistancesProps) => {
  return (
    <div
      {...rest}
      className={classNames(styles.component, {
        [styles.hasFourItems]: items.length >= 4,
        [styles.hasSevenItems]: items.length >= 7,
        [styles.hasTenItems]: items.length >= 10,
      })}
    >
      <div className={styles.wrapper}>
        {(headline || text) && (
          <div className={styles.textWrapper}>
            <Typography className={styles.headline} tagStyle="headingMedium">
              {headline}
            </Typography>

            {text && <RichText text={text} />}
          </div>
        )}

        {items.length > 0 && (
          <div className={styles.items}>
            <div className={styles.lineWrapper}>
              <span className={styles.line}>
                <span className={styles.lineBefore} />

                <span className={styles.lineAfter} />
              </span>

              <span className={styles.line}>
                <span className={styles.lineBefore} />

                <span className={styles.lineAfter} />
              </span>

              <span className={styles.line}>
                <span className={styles.lineBefore} />

                <span className={styles.lineAfter} />
              </span>

              <span className={styles.line}>
                <span className={styles.lineBefore} />

                <span className={styles.lineAfter} />
              </span>

              <div className={styles.lineEnding} />

              <div className={styles.lineIconWrapper}>
                <Icon name="Bed" size={12} />
              </div>
            </div>

            <div className={styles.itemsWrapper}>
              {items.map((item, itemIndex) => (
                <div key={itemIndex} className={styles.item}>
                  <div className={styles.itemHeader}>
                    <Icon
                      className={styles.itemHeaderIcon}
                      name={item.icon as IconName}
                      size={24}
                    />

                    <Typography
                      className={styles.itemHeaderLabel}
                      tagStyle="bodySmall"
                    >
                      {item.name}
                    </Typography>
                  </div>

                  <div className={styles.itemFooter}>
                    <Typography
                      className={styles.itemFooterLabel}
                      tagStyle="distance"
                    >
                      {item.value}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {link && (
          <CtaLinkList
            items={[
              {
                $type: 'LinkCtaDto',
                link,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};

HotelDistances.displayName = 'HotelDistances';

export default HotelDistances;
